import { useEffect, useState } from 'react';
import { parse } from 'graphql';

import { createAuthenticatedGraphQLClient } from '../auth/createAuthenticatedGraphQLClient';
import { resolveClientEnv } from '../graphql/config/resolveClientEnv';
import type {
  DoorVisitFilterOptionsQuery,
  DoorVisitFilterOptionsQueryVariables,
} from '../graphql/queries/doorVisitFilterOptions.generated';

type FetchImpl = Parameters<typeof createAuthenticatedGraphQLClient>[0]['fetchImpl'];

export type DoorVisitCustomSlotFilterOption = DoorVisitFilterOptionsQuery['doorVisitFilterOptions']['customSlots'][number];

export type UseDoorVisitFilterOptionsOptions = {
  fetchImpl?: FetchImpl;
};

export type UseDoorVisitFilterOptionsResult = {
  canvasserIds: string[];
  customSlots: DoorVisitCustomSlotFilterOption[];
  loading: boolean;
  error: string | null;
  refetch: () => void;
};

export const DOOR_VISIT_FILTER_OPTIONS_DOCUMENT = parse(/* GraphQL */ `
  query DoorVisitFilterOptions {
    doorVisitFilterOptions {
      canvasserIds
      customSlots {
        name
        label
        values {
          value
          label
        }
      }
    }
  }
`);

// Account-scoped filter option lists — fetched once per
// mount, independent of the paginated/filtered list itself so changing a
// filter never narrows the options offered for the others.
export const useDoorVisitFilterOptions = ({
  fetchImpl,
}: UseDoorVisitFilterOptionsOptions = {}): UseDoorVisitFilterOptionsResult => {
  const [canvasserIds, setCanvasserIds] = useState<string[]>([]);
  const [customSlots, setCustomSlots] = useState<DoorVisitCustomSlotFilterOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refetchCounter, setRefetchCounter] = useState(0);

  useEffect(() => {
    let cancelled = false;

    const run = async (): Promise<void> => {
      setLoading(true);
      setError(null);

      try {
        const env = resolveClientEnv();
        const client = createAuthenticatedGraphQLClient({ env, fetchImpl });

        const variables: DoorVisitFilterOptionsQueryVariables = {};
        const data = await client.request<DoorVisitFilterOptionsQuery>({
          document: DOOR_VISIT_FILTER_OPTIONS_DOCUMENT,
          variables,
        });

        if (!cancelled) {
          setCanvasserIds(data.doorVisitFilterOptions.canvasserIds);
          setCustomSlots(data.doorVisitFilterOptions.customSlots);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load filter options.');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    void run();

    return () => {
      cancelled = true;
    };
  }, [fetchImpl, refetchCounter]);

  const refetch = (): void => {
    setRefetchCounter(c => c + 1);
  };

  return { canvasserIds, customSlots, loading, error, refetch };
};
